import appconfig from '@/appconfig';
import { fetchJson, handleError } from '@/lib/http/http';
import type { Botconfig } from '@/types';

/**
 * Fetches the bot configuration from the API.
 * @param botId - The ID of the bot.
 * @param channelId - The optional ID of the channel.
 * @returns A promise that resolves to the bot configuration.
 * @throws Will throw an error if the botId is not provided or if the fetch fails.
 */
export const fetchConfig = async (botId: string, channelId?: string): Promise<Botconfig> => {
  if (!botId) throw new Error('No BotId to fetch config');
  let urlString = channelId
    ? `${appconfig.apiPath}/widget/config/${botId}/${channelId}`
    : `${appconfig.apiPath}/widget/config/${botId}`;

  // We can overwrite the config in development mode with a query parameter
  // it will link to the config endpoint with the given config name in /configs folder
  // the request will handled by a vite server proxy you can find in the vite config
  if (appconfig.isDevelopment) {
    const localConfig = new URL(window.parent.location.href).searchParams.get('config');
    if (localConfig || localConfig === '') {
      urlString = `${window.parent.location.origin}/config?config=${localConfig ?? 'default'}`;
    }
  }
  try {
    const url = new URL(urlString);
    return await fetchJson<Botconfig>(url.toString(), {headers: {'Content-Type': 'application/json; charset=utf-8',},});
  } catch (error: unknown) {
    throw handleError(error);
  }
}
interface TeaserApiResponse {
  channelId: string;
  showTeaser: boolean;
  showAvatar: boolean;
  message: string;
  displayName: string;
  timeout: number;
  quickActions: {
    id: string;
    text: string;
    intent: string;
    targetIntent: string;
  }[];
}

/**
 * Fetches the teaser from the API.
 * @param botId - The ID of the bot.
 * @param channelId - The ID of the channel.
 * @param isMobile - A boolean indicating if the request is from a mobile device.
 * @returns A promise that resolves to the teaser.
 * @throws Will throw an error if no teaser is available or if the fetch fails.
 */
export const fetchTeaser = async (botId: string, channelId: string, isMobile: boolean, originUrl: string): Promise<TeaserApiResponse> => {
  const url = new URL(`${appconfig.apiPath}/widget/teaser/${botId}/${channelId}`);
  url.searchParams.append('isMobile', isMobile.toString());
  url.searchParams.append('originUrl', originUrl);
  try {
    const data = await fetchJson<{ teaser: TeaserApiResponse }>(url.toString());
    if (data && !data.teaser) {
      throw new Error('No teaser available');
    }
    return data.teaser;

  } catch (error: unknown) {
    throw handleError(error);
  }
}
